import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { REGIONS, getRegionLabel } from '../lib/regions';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, 
  User, 
  Mail,
  Edit,
  Save,
  X,
  Plus,
  Loader2,
  Calendar,
  CheckCircle,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_SUGGESTIONS = [
  'Technology', 'Web Development', 'Tutoring', 'Language Teaching',
  'Cooking', 'Gardening', 'Music', 'Art', 'Photography', 'Writing',
  'Childcare', 'Pet Care', 'Home Repair', 'Transportation', 'Administrative',
  'Legal Advice', 'Financial Planning', 'Health & Wellness', 'Crafts'
];

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminCategories, setAdminCategories] = useState(FALLBACK_SUGGESTIONS);
  const [profileData, setProfileData] = useState({
    name: '',
    bio: '',
    skills: [],
    availability: '',
    home_region: '',
    business_name: '',
    business_category: ''
  });
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        bio: user.bio || '',
        skills: user.skills || [],
        availability: user.availability || '',
        home_region: user.home_region || '',
        business_name: user.business_name || '',
        business_category: user.business_category || ''
      });
    }
    axios.get(`${API_URL}/api/site-content`)
      .then(res => {
        if (res.data?.categories?.length) setAdminCategories(res.data.categories);
      })
      .catch(() => {});
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/users/me`, profileData, { withCredentials: true });
      await refreshUser();
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (newSkill && !profileData.skills.includes(newSkill)) {
      setProfileData({
        ...profileData,
        skills: [...profileData.skills, newSkill]
      });
      setNewSkill('');
    }
  };

  const addSkillFromCategory = (cat) => {
    if (cat && !profileData.skills.includes(cat)) {
      setProfileData({
        ...profileData,
        skills: [...profileData.skills, cat]
      });
    }
  };

  const removeSkill = (skillToRemove) => {
    setProfileData({
      ...profileData,
      skills: profileData.skills.filter(s => s !== skillToRemove)
    });
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
        {/* Profile Header */}
        <Card className="mb-6">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <Avatar className="w-24 h-24 border-4 ">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#d1c7e9] text-white text-2xl">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-semibold text-[#090000]">
                      {user?.account_type === 'business' && user?.business_name ? user.business_name : user?.name}
                    </h1>
                    {user?.account_type === 'business' && user?.name && (
                      <p className="text-sm text-black/60 font-sans -mt-1">Contact: {user.name}</p>
                    )}
                    <p className="text-[#090000/60] flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {user?.email}
                    </p>
                  </div>
                  {!editing ? (
                    <Button 
                      onClick={() => setEditing(true)} 
                      variant="outline" 
                      className=""
                      data-testid="edit-profile-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => {
                          setEditing(false);
                          setProfileData({
                            name: user?.name || '',
                            bio: user?.bio || '',
                            skills: user?.skills || [],
                            availability: user?.availability || '',
                            home_region: user?.home_region || ''
                          });
                        }} 
                        variant="outline" 
                        className=""
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSave} 
                        className="bg-[#ffd703] hover:bg-[#e6c203]"
                        disabled={loading}
                        data-testid="save-profile-btn"
                      >
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 mt-4">
                  {user?.account_type === 'business' && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#d8b4fe] border-2 border-black rounded-full">
                      <Building2 className="w-4 h-4 text-black" />
                      <span className="text-sm font-semibold text-black" data-testid="profile-business-badge">
                        Business {user?.business_category ? `· ${user.business_category}` : ''}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#ffd703]/10 rounded-full">
                    <Clock className="w-4 h-4 text-[#ffd703]" />
                    <span className="text-sm font-medium text-[#ffd703]" data-testid="profile-credits">
                      {user?.time_credits?.toFixed(1) || '0.0'} credits
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#d1c7e9]/10 rounded-full">
                    <Calendar className="w-4 h-4 text-[#d1c7e9]" />
                    <span className="text-sm font-medium text-[#d1c7e9]">
                      Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Bio & Availability */}
          <Card className="">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-[#090000]">About</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <>
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      className=""
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea
                      value={profileData.bio}
                      onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                      placeholder="Tell the community about yourself..."
                      className=""
                      rows={4}
                      data-testid="profile-bio-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Availability</Label>
                    <Input
                      value={profileData.availability}
                      onChange={(e) => setProfileData({...profileData, availability: e.target.value})}
                      placeholder="e.g., Weekday evenings, Saturday mornings"
                      className=""
                      data-testid="profile-availability-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Home Dashboard (St. Louis region)</Label>
                    <Select
                      value={profileData.home_region || 'none'}
                      onValueChange={(v) => setProfileData({...profileData, home_region: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger data-testid="profile-region-select">
                        <SelectValue placeholder="Choose your default dashboard" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No default —</SelectItem>
                        {REGIONS.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-black/60 font-sans">New offers and requests you post will default to this dashboard.</p>
                  </div>
                  {user?.account_type === 'business' && (
                    <div className="space-y-2 p-3 rounded-md border-2 border-black bg-[#d8b4fe]/20">
                      <div className="flex items-center gap-2 text-sm font-sans font-semibold">
                        <Building2 className="w-4 h-4" /> Business info
                      </div>
                      <div>
                        <Label className="text-xs font-sans font-semibold">Business Name</Label>
                        <Input
                          value={profileData.business_name}
                          onChange={(e) => setProfileData({...profileData, business_name: e.target.value})}
                          placeholder="Corner Bakery"
                          data-testid="profile-business-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-sans font-semibold">Business Category</Label>
                        <Input
                          value={profileData.business_category}
                          onChange={(e) => setProfileData({...profileData, business_category: e.target.value})}
                          placeholder="e.g. Restaurant / Food"
                          data-testid="profile-business-category-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-[#090000/60]">Bio</Label>
                    <p className="mt-1 text-[#090000]">
                      {user?.bio || 'No bio added yet.'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-[#090000/60]">Availability</Label>
                    <p className="mt-1 text-[#090000]">
                      {user?.availability || 'Not specified.'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-[#090000/60]">Home Dashboard</Label>
                    <p className="mt-1 text-[#090000]" data-testid="profile-region-display">
                      {user?.home_region ? getRegionLabel(user.home_region) : 'Not set.'}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Skills */}
          <Card className="">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-[#090000]">Skills</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="font-sans font-semibold text-xs mb-1 block">Pick from community categories</Label>
                    <Select onValueChange={addSkillFromCategory}>
                      <SelectTrigger data-testid="skill-category-select">
                        <SelectValue placeholder="Select a category to add…" />
                      </SelectTrigger>
                      <SelectContent>
                        {adminCategories
                          .filter(c => !profileData.skills.includes(c))
                          .map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-sans font-semibold text-xs mb-1 block">Or add a custom skill</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        placeholder="e.g. French translation"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        data-testid="add-skill-input"
                      />
                      <Button 
                        type="button" 
                        onClick={addSkill} 
                        variant="outline" 
                        data-testid="add-skill-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profileData.skills.map((skill) => (
                      <Badge 
                        key={skill} 
                        className="bg-[#ffd703] text-black px-3 py-1 flex items-center gap-1"
                        data-testid={`skill-tag-${skill}`}
                      >
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)} className="ml-1 hover:text-red-600" aria-label={`Remove ${skill}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {user?.skills?.length > 0 ? (
                    user.skills.map((skill) => (
                      <Badge key={skill} className="bg-[#ffd703]/10 text-[#ffd703] px-3 py-1">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-[#090000/60]">No skills added yet.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
